import { apiClient } from './client';

/**
 * Client for `/api/integrations/*` (Phase 11 Day 2). Stays in
 * lockstep with `server/src/routes/integrations.routes.ts`.
 */

export type ProviderCategory = 'fulfillment' | 'payroll' | 'accounting' | 'comms';
export type ProviderAuthType = 'api_key' | 'oauth2' | 'basic' | 'chatwoot';

export interface ProviderMetricKey {
  key: string;
  label: string;
  defaultDirection: 'higher_is_better' | 'lower_is_better';
}

export interface ProviderCatalogItem {
  id: string;
  displayName: string;
  category: ProviderCategory;
  authType: ProviderAuthType;
  availableMetrics: ProviderMetricKey[];
  isImplemented: boolean;
}

export type IntegrationStatus = 'active' | 'disconnected' | 'error';

export interface Integration {
  id: string;
  provider: string;
  displayName: string;
  status: IntegrationStatus;
  lastSyncAt: string | null;
  lastSyncStatus: IntegrationStatus | null;
  lastSyncError: string | null;
  createdAt: string;
}

export interface IntegrationSyncLog {
  id: string;
  integrationId: string;
  status: IntegrationStatus;
  recordsSynced: number;
  error: string | null;
  metricsTouched: string[];
  startedAt: string;
  completedAt: string | null;
}

export async function listProviderCatalog(): Promise<ProviderCatalogItem[]> {
  const res = await apiClient.get<{ data: ProviderCatalogItem[] }>('/api/integrations/providers');
  return res.data.data;
}

export async function listIntegrations(): Promise<Integration[]> {
  const res = await apiClient.get<{ data: Integration[] }>('/api/integrations');
  return res.data.data;
}

export type ConnectIntegrationRequest =
  | {
      providerId: string;
      displayName?: string;
      kind: 'api_key';
      apiKey: string;
    }
  | {
      providerId: string;
      displayName?: string;
      kind: 'oauth2';
      accessToken: string;
      refreshToken?: string | null;
      expiresAt?: string | null;
    }
  | {
      // Chatwoot (Phase 16 / ADR-026). The server mints the webhook
      // token and returns `webhookPath` for pasting into Chatwoot.
      providerId: string;
      displayName?: string;
      kind: 'chatwoot';
      baseUrl: string;
      apiAccessToken: string;
      accountId: number;
      webhookHmacSecret?: string;
    };

/** Chatwoot connects additionally return the webhook path to paste into Chatwoot. */
export type ConnectIntegrationResponse = Integration & { webhookPath?: string };

export async function connectIntegration(
  body: ConnectIntegrationRequest,
): Promise<ConnectIntegrationResponse> {
  const res = await apiClient.post<{ data: ConnectIntegrationResponse }>('/api/integrations', body);
  return res.data.data;
}

export async function disconnectIntegration(id: string): Promise<void> {
  await apiClient.delete(`/api/integrations/${encodeURIComponent(id)}`);
}

export interface SyncResult {
  syncLogId: string;
  status: 'active' | 'error';
  recordsSynced: number;
  metricsTouched: string[];
  error: string | null;
}

export async function syncIntegrationNow(id: string): Promise<SyncResult> {
  const res = await apiClient.post<{ data: SyncResult }>(`/api/integrations/${encodeURIComponent(id)}/sync`);
  return res.data.data;
}

export async function listSyncLogs(integrationId: string): Promise<IntegrationSyncLog[]> {
  const res = await apiClient.get<{ data: IntegrationSyncLog[] }>(
    `/api/integrations/${encodeURIComponent(integrationId)}/syncs`,
  );
  return res.data.data;
}
